import { useCallback, useRef, useState, useEffect } from 'react';
import { clearCrmPhonePause } from '@/shared/auth/auth.reducer';
import { coercePhonePauseFlag, computeAcwElapsedFromAnchor, computePhonePauseTimers, isProfileAcwActive, parsePhonePauseEndMs, } from '@/shared/model/phone-pause';
import { syncAcwProfileAfterHangup } from '@/shared/services/syncAcwProfileAfterHangup';
import { useAppDispatch, useAppSelector } from '@/store';
/**
 * ACW / phone pause UI timers.
 *
 * - Profile `isPhonePause` / `phonePauseEnd` is loaded on login and page refresh (`GET /api/users/profile`).
 * - After hangup, refetch profile so ACW countdown matches PBX `phonePauseEnd`.
 * - Manual "ready for calls" calls `PUT /api/phone-pause/clear` and clears local pause flags.
 */
export function useBackendPhonePause() {
    const dispatch = useAppDispatch();
    const crmProfile = useAppSelector((s) => s.auth.crmProfile);
    const pauseAnchorRef = useRef(null);
    const [timers, setTimers] = useState(() => computePhonePauseTimers(crmProfile));
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    useEffect(() => {
        const tick = () => {
            const next = computePhonePauseTimers(crmProfile);
            setTimers(next);
            if (next.isPaused && next.endsAtMs != null) {
                const anchor = pauseAnchorRef.current;
                if (!anchor || anchor.endsAtMs !== next.endsAtMs) {
                    pauseAnchorRef.current = {
                        endsAtMs: next.endsAtMs,
                        initialRemainingSec: next.remainingSeconds,
                    };
                }
                const activeAnchor = pauseAnchorRef.current;
                setElapsedSeconds(activeAnchor
                    ? computeAcwElapsedFromAnchor(activeAnchor.initialRemainingSec, next.remainingSeconds)
                    : next.elapsedSeconds);
            }
            else {
                pauseAnchorRef.current = null;
                setElapsedSeconds(0);
            }
        };
        tick();
        const id = window.setInterval(tick, 1000);
        return () => window.clearInterval(id);
    }, [crmProfile]);
    /** When `phonePauseEnd` passes (or is missing), drop stale `isPhonePause` so toolbar shows idle. */
    useEffect(() => {
        if (!coercePhonePauseFlag(crmProfile?.isPhonePause))
            return;
        const clearStalePause = () => dispatch(clearCrmPhonePause());
        const endsAtMs = parsePhonePauseEndMs(crmProfile?.phonePauseEnd);
        if (endsAtMs == null) {
            clearStalePause();
            return;
        }
        const msUntilEnd = endsAtMs - Date.now();
        if (msUntilEnd <= 0) {
            clearStalePause();
            return;
        }
        const id = window.setTimeout(clearStalePause, msUntilEnd);
        return () => window.clearTimeout(id);
    }, [crmProfile?.isPhonePause, crmProfile?.phonePauseEnd, dispatch]);
    /** Sync Redux when the local countdown reaches zero before the timeout fires. */
    useEffect(() => {
        if (coercePhonePauseFlag(crmProfile?.isPhonePause) && !isProfileAcwActive(crmProfile)) {
            dispatch(clearCrmPhonePause());
        }
    }, [crmProfile, dispatch]);
    /** PBX pause after hangup — read `isPhonePause` / `phonePauseEnd` from profile API. */
    const syncAcwFromProfileAfterHangup = useCallback(() => {
        void syncAcwProfileAfterHangup(dispatch);
    }, [dispatch]);
    /** After manual unpause API succeeds — clear Redux pause flags (no profile GET). */
    const endLocalAcw = useCallback(() => {
        pauseAnchorRef.current = null;
        dispatch(clearCrmPhonePause());
    }, [dispatch]);
    return {
        isOnBackendAcw: timers.isPaused,
        acwRemainingSeconds: timers.remainingSeconds,
        acwElapsedSeconds: elapsedSeconds,
        acwEndsAtMs: timers.endsAtMs,
        syncAcwFromProfileAfterHangup,
        endLocalAcw,
    };
}
